import type {
  DeRegisterDeviceReqBody,
  DeviceDTO,
  GetDevicesQuery,
  GetDevicesResponse,
  GetDeviceUsageResponse,
  RegisterDeviceReqBody,
  UpdateDeviceNotesInputBodySchema,
  UpdateDeviceNotesInputParamSchema,
  UpdateDeviceNotesResponse,
} from '@eppendorf/vnls-device-service-utils';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';

import { fetcher } from '$shared/fetch';
import {
  createDevicePath,
  createPathWithQueryParams,
  objectToURLSearchParams,
  SENSE_DEVICES_BASE_PATH,
  OTA_DEVICES_BASE_PATH,
  DEVICE_SERVICE_BASE_PATH,
} from '$shared/utils/api-paths';

import {
  AssociatedRouterQueryResult,
  PublishDeviceUpdateResponse,
  SenseDevice,
} from '$features/devices/devices.types';
import { deviceSnapshotQueryKey } from '$features/monitoring/device-snapshot.api';
import { devicesv2QueryKey } from '$features/monitoring/v2/devicesv2.api';

export const devicesQueryKey = 'devices';
export const addedDeviceQueryKey = 'addedDevice';
export const deletedDeviceQueryKey = 'deletedDevice';
export const associationListQueryKey = 'associationList';
export const otaUpdate = 'otaUpdate';

export const createDevicesQueryWithQueryParams = (queryParams: GetDevicesQuery) => ({
  queryKey: [devicesQueryKey, ...objectToURLSearchParams(queryParams).values()],
  queryFn: () =>
    fetcher.get<GetDevicesResponse>(
      createPathWithQueryParams(DEVICE_SERVICE_BASE_PATH, queryParams),
    ),
  placeholderData: keepPreviousData,
});

export const useDevicesWithQueryParams = (queryParams: GetDevicesQuery) =>
  useQuery(createDevicesQueryWithQueryParams(queryParams));

export const createDeviceQuery = ({
  manufacturer,
  serialNumber,
  enabled = true,
}: Pick<DeviceDTO, 'manufacturer' | 'serialNumber'> & { enabled?: boolean }) => ({
  queryKey: [devicesQueryKey, manufacturer, serialNumber],
  queryFn: () => fetcher.get<DeviceDTO>(createDevicePath(manufacturer, serialNumber)),
  enabled: !!enabled,
});

export const useGetDevice = (
  deviceSearch: Pick<DeviceDTO, 'manufacturer' | 'serialNumber'> & { enabled?: boolean },
) => useQuery(createDeviceQuery(deviceSearch));

export const useAddedDevices = () =>
  useQuery({
    queryKey: [addedDeviceQueryKey],
    queryFn: () => [],
  });

export const useAddDevice = () => {
  const addDevice = (device: RegisterDeviceReqBody) =>
    fetcher.post<RegisterDeviceReqBody, unknown>(
      `${DEVICE_SERVICE_BASE_PATH}/actions/register`,
      device,
    );
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<unknown>, AxiosError, RegisterDeviceReqBody>({
    mutationFn: addDevice,
    // This is sufficient for now. When we tackle performance we can utilise
    // optimistic updates or saving another single device request by mutating data instead
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [devicesQueryKey] });
      queryClient.invalidateQueries({ queryKey: [devicesv2QueryKey] });

      queryClient.setQueryData([addedDeviceQueryKey], variables.serialNumber);
    },
  });
};

export const useReregisterDevice = () => {
  const addDevice = (device: RegisterDeviceReqBody) =>
    fetcher.post<RegisterDeviceReqBody, unknown>(
      `${DEVICE_SERVICE_BASE_PATH}/actions/reregister`,
      device,
    );

  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<unknown>, AxiosError, RegisterDeviceReqBody>({
    mutationFn: addDevice,
    // This is sufficient for now. When we tackle performance we can utilise
    // optimistic updates or saving another single device request by mutating data instead
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [devicesQueryKey] });
      queryClient.invalidateQueries({ queryKey: [devicesv2QueryKey] });

      queryClient.setQueryData([addedDeviceQueryKey], variables.serialNumber);
    },
  });
};

export const useUpdateDevice = () => {
  const updateDevice = (device: DeviceDTO) =>
    fetcher.patch<DeviceDTO, DeviceDTO>(
      createDevicePath(device.manufacturer, device.serialNumber),
      device,
    );
  const queryClient = useQueryClient();
  return useMutation<DeviceDTO, Error, DeviceDTO>({
    mutationFn: updateDevice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [devicesQueryKey] });
      queryClient.invalidateQueries({ queryKey: [devicesv2QueryKey] });
      queryClient.invalidateQueries({ queryKey: [deviceSnapshotQueryKey] });
    },
  });
};

export const useUpdateDeviceNotes = () => {
  const updateDeviceNotes = ({
    device,
    body,
  }: {
    device: UpdateDeviceNotesInputParamSchema;
    body: UpdateDeviceNotesInputBodySchema;
  }) =>
    fetcher.patch<UpdateDeviceNotesInputBodySchema, UpdateDeviceNotesResponse>(
      `${createDevicePath(device.manufacturer, device.serialNumber)}/notes`,
      body,
    );

  const queryClient = useQueryClient();
  return useMutation<
    UpdateDeviceNotesResponse,
    Error,
    { device: UpdateDeviceNotesInputParamSchema; body: UpdateDeviceNotesInputBodySchema }
  >({
    mutationFn: updateDeviceNotes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [devicesQueryKey] });
      queryClient.invalidateQueries({ queryKey: [devicesv2QueryKey] });
    },
  });
};

export const useDeleteDevice = () => {
  const deleteDevice = (device: DeRegisterDeviceReqBody) =>
    fetcher.post<DeRegisterDeviceReqBody, never>(
      `${DEVICE_SERVICE_BASE_PATH}/actions/deregister`,
      device,
      {
        headers: {
          'x-api-key': 'Easterly-nitrogen-involute-deride',
        },
      },
    );

  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<unknown>, AxiosError, DeRegisterDeviceReqBody>({
    mutationFn: deleteDevice,
    // This is sufficient for now. When we tackle performance we can utilise
    // optimistic updates or saving another single device request by mutating data instead
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [devicesQueryKey] });
      queryClient.invalidateQueries({ queryKey: [devicesv2QueryKey] });
    },
  });
};

export const useGetSenseDevice = (deviceId: string, deviceType: 'gateways' | 'monitor') =>
  useQuery({
    queryKey: ['sense-monitor', deviceId],
    queryFn: () =>
      fetcher.get<SenseDevice>(`${SENSE_DEVICES_BASE_PATH}/${deviceType}/${deviceId}`),
    refetchInterval: 30000,
  });

export const useGetMonitorAssociations = (monitorId: string) =>
  useQuery({
    queryKey: ['sense-gateway', monitorId],
    queryFn: () =>
      fetcher.get<AssociatedRouterQueryResult[]>(
        `${SENSE_DEVICES_BASE_PATH}/monitors/${monitorId}/associated-gateways`,
      ),
    refetchInterval: 30000,
  });

export type BlockMonitorParams = {
  monitorId: string;
  isBlocked: boolean;
};

export type UpdateAssociationListResponse = {
  blocked: string[];
  serialNumber: string;
  tenantId: string;
  type: string;
};

export type AssociationList = {
  tenantId: string;
  serialNumber: string;
  type: string;
  blocked: Array<string>;
};

export const useGetGatewayAssociationList = (gatewaySerialnumber: string) =>
  useQuery({
    queryKey: [associationListQueryKey, gatewaySerialnumber],
    queryFn: () =>
      fetcher.get<AssociationList>(
        `${SENSE_DEVICES_BASE_PATH}/gateways/${gatewaySerialnumber}/association-list`,
      ),
    refetchInterval: 30000,
  });

export const useUpdateMonitorBlockedState = (gatewaySerialNumber: string) => {
  const blockMonitor = (blockMonitorParams: BlockMonitorParams) =>
    fetcher.patch<BlockMonitorParams, never>(
      `${SENSE_DEVICES_BASE_PATH}/gateways/${gatewaySerialNumber}/association-list`,
      blockMonitorParams,
    );

  const queryClient = useQueryClient();
  return useMutation<
    AxiosResponse<UpdateAssociationListResponse>,
    AxiosError,
    BlockMonitorParams
  >({
    mutationFn: blockMonitor,
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: [associationListQueryKey] }),
  });
};

export const createGetDeviceUpdateStatusQueryWithQueryParams = <T>({
  enabled,
  ...queryParams
}: {
  deviceTypeId: string;
  enabled?: boolean;
  softwareVersion?: string;
  hardwareRevision?: string;
}) => ({
  queryKey: [otaUpdate, ...objectToURLSearchParams(queryParams).values()],
  queryFn: () =>
    fetcher.get<T>(
      createPathWithQueryParams(
        `${OTA_DEVICES_BASE_PATH}/${queryParams.softwareVersion ? 'update-v2' : 'update'}`,
        queryParams,
      ),
    ),
  enabled,
});

export const useGetDeviceUpdateStatusWithQueryParams = <T>(options: {
  deviceTypeId: string;
  softwareVersion?: string;
  enabled?: boolean;
  hardwareRevision?: string;
}) => useQuery(createGetDeviceUpdateStatusQueryWithQueryParams<T>(options));

export const useOtaUpdateDevice = () => {
  const updateOta = (deviceTypeId: string) =>
    fetcher.post<{ deviceTypeId: string }, PublishDeviceUpdateResponse>(
      `${OTA_DEVICES_BASE_PATH}/update`,
      {
        deviceTypeId,
      },
    );

  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<PublishDeviceUpdateResponse>, AxiosError, string>({
    mutationFn: updateOta,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [otaUpdate] });
    },
  });
};

export const deviceUsageQueryKey = [devicesQueryKey, 'device-usage'];

export const useGetDeviceUsage = (enabled = true) =>
  useQuery({
    queryKey: deviceUsageQueryKey,
    queryFn: () =>
      fetcher.get<GetDeviceUsageResponse>(`${DEVICE_SERVICE_BASE_PATH}/device-usage`),
    enabled,
  });
